import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { DialogClass, DialogService } from '@services/dialog';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback';
import { ProgressIndicatorService } from '@services/progress';

import { I18nService } from '../../../../../../i18n/services';
import { AppKeyService, IAppKey } from '../../../../../../services/appkey';
import { LegalAssociateAppKey, LegalAssociateAppKeyDialogComponent } from '../legal-associate-appkey-dialog';
import { LegalAppKeyAssociation } from './legal-appkey-association.models';
import { LegalsService } from '../../../../../../services/legals';

@Component({
	selector: 'app-legal-appkey-association',
	templateUrl: './legal-appkey-association.dialog.html'
})
export class LegalAppKeyAssociationDialogComponent implements OnInit {
	public searchControl = this.formBuilder.control('');
	public unassignedAppKeys: IAppKey[] = [];
	public appKeys: IAppKey[] = [];
	public appKeysFiltered: IAppKey[] = [];
	public pageIndex = 0;
	public pageSize = 10;
	public pageResults = 0;
	public loading = false;
	public hasActive = false;
	public openedMenu: number;

	constructor(
		private dialogService: DialogService,
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService,
		@Inject(MAT_DIALOG_DATA) public dialogData: LegalAppKeyAssociation,
		private dialogRef: MatDialogRef<LegalAppKeyAssociationDialogComponent>,
		private appKeyService: AppKeyService,
		private formBuilder: FormBuilder,
		private i18nService: I18nService,
		private legalsService: LegalsService
	) {}

	public ngOnInit(): void {
		this.dialogRef.afterClosed().subscribe(() => this.dialogData.callback());
		this.hasActive = this.dialogData.terms?.activeVersion ? true : false;
		this.searchControl.valueChanges.subscribe(() => this.filterAppKeys(null, true));
		this.getAppKeys();
	}

	public filterAppKeys(event: PageEvent, resetPageIndex = false): void {
		this.pageIndex = resetPageIndex || event?.pageSize !== this.pageSize ? 0 : event ? event.pageIndex : this.pageIndex;
		this.pageSize = event ? event.pageSize : this.pageSize;

		const appKeys = this.appKeys.filter((appKey) => {
			return this.searchControl.value && this.searchControl.value.trim()
				? appKey.clientId.toLowerCase().includes(this.searchControl.value.toLowerCase().trim()) ||
						appKey.name.toLowerCase().includes(this.searchControl.value.toLowerCase().trim())
				: true;
		});

		this.pageResults = appKeys.length;
		this.appKeysFiltered = appKeys.slice(this.pageIndex * this.pageSize, this.pageIndex * this.pageSize + this.pageSize);
	}

	public associateAppKeys(): void {
		const data: LegalAssociateAppKey = {
			company: this.dialogData.company,
			terms: this.dialogData.terms,
			appKeys: this.unassignedAppKeys,
			callback: () => {
				this.getAppKeys(true);
			}
		};

		this.dialogService.open(LegalAssociateAppKeyDialogComponent, {
			panelClass: DialogClass.medium,
			data
		});
	}

	public unassociateAppKey(appKey: IAppKey): void {
		this.feedbackService.confirm({
			title: FeedbackTitle.attention,
			message: this.i18nService.get(
				'This will disassociate the legal document from the app key. Are you sure you want to continue?',
				'app.prompt.legal.disassociate-terms'
			),
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) {
					this.progressIndicatorService.spinner(true);

					this.legalsService
						.unassociateTermsFromAppKeys(appKey.clientId, [this.dialogData.terms.termsId])
						.subscribe(
							() => {
								this.appKeyService.data.appKeys[appKey.clientId].requiredTerms =
									this.appKeyService.data.appKeys[appKey.clientId].requiredTerms.filter(
										(requiredTerm) => requiredTerm.termsId !== this.dialogData.terms.termsId
									);

								this.getAppKeys();
								this.progressIndicatorService.spinner(false);
							},
							(error: any) => {
								this.feedbackService.apiError(error);
								this.progressIndicatorService.spinner(false);
							}
						);
				}
			}
		});
	}

	private getAppKeys(force = false): void {
		this.progressIndicatorService.spinner(true);
		this.loading = true;

		this.appKeyService.getAll(force).subscribe(
			(res: { [clientId: string]: IAppKey }) => {
				this.appKeys = [];
				this.unassignedAppKeys = [];

				Object.values(res)
					.sort((a, b) => {
						return a.name.localeCompare(b.name);
					})
					.forEach((appKey) => {
						if (appKey.requiredTerms.findIndex((term) => term.termsId === this.dialogData.terms.termsId) > -1) {
							this.appKeys.push(appKey);
						} else {
							this.unassignedAppKeys.push(appKey);
						}
					});

				this.filterAppKeys(null, true);
				this.progressIndicatorService.spinner(false);
				this.loading = false;
			},
			(error: any) => {
				this.feedbackService.apiError(error);
				this.dialogRef.close();
				this.progressIndicatorService.spinner(false);
			}
		);
	}
}
